import React, { useEffect, useState } from "react";
import { Card, Row, Col, notification, Empty, Select, Spin } from "antd";
import { useSelector } from "react-redux";
import { apiHandle } from "../../config/apiHandle";
import {
  error_toast_message,
  success_toast_message,
} from "../../utils/toast_message";
import moment from "moment";
import LogoUpload from "./LogoUpload";
import StoreForm from "./StoreForm";
import StorePreviewCore from "./StorePreviewCore";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";

const { Option } = Select;

const Store = () => {
  const location = useLocation();
  const [api, contextHolder] = notification.useNotification();
  const [imageLoading, setImageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [idExist, setIdExist] = useState(null);
  const [statusHandle, setStatusHandle] = useState(null);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [storeData, setStoreData] = useState({
    name: "",
    description: "",
    contactEmail: "",
    contactPhones: [""],
    benefits: [""],
    address: "",
    location: "",
    city: "",
    operatingHours: {
      Monday: { open: null, close: null },
      Tuesday: { open: null, close: null },
      Wednesday: { open: null, close: null },
      Thursday: { open: null, close: null },
      Friday: { open: null, close: null },
      Saturday: { open: null, close: null },
      Sunday: { open: null, close: null },
    },
    delivery_radius: null,
    logo: "",
    operationalDays: [],
    status: "Active",
  });

  const { user } = useSelector((state) => state.userAuth);
  const storePermissions = user?.permissions?.stores || {};
  const canCreate = storePermissions.create;
  const canUpdate = storePermissions.update;
  const canRead = storePermissions.read;

  useEffect(() => {
    if (canRead) fetchStoreData();
  }, [location.state, canRead]);

  const fetchStoreData = async () => {
    try {
      const adminRoute = `get-my-store/${location.state}`;
      const vendorRoute = `get-my-store/${user._id}`;
      const response = await apiHandle.get(
        user?.role === "Admin" ? adminRoute : vendorRoute
      );

      if (response.data) {
        setVendorDetails(response.data?.vendor);
        const store = response.data?.data;
        setIdExist(store?._id);
        setStatusHandle(store.status);

        // Initialize operating hours
        const operatingHours = {
          Monday: { open: null, close: null },
          Tuesday: { open: null, close: null },
          Wednesday: { open: null, close: null },
          Thursday: { open: null, close: null },
          Friday: { open: null, close: null },
          Saturday: { open: null, close: null },
          Sunday: { open: null, close: null },
        };

        // Populate with existing data if available
        if (store.operatingHours) {
          Object.keys(store.operatingHours).forEach((day) => {
            operatingHours[day] = {
              open: store.operatingHours[day].open
                ? moment(store.operatingHours[day].open, "h:mm A")
                : null,
              close: store.operatingHours[day].close
                ? moment(store.operatingHours[day].close, "h:mm A")
                : null,
            };
          });
        }

        const formattedData = {
          vendor_id: store?.vendor_id || null,
          name: store.name || "",
          description: store.description || "",
          contactEmail: store.contactEmail || "",
          contactPhones: store.contactPhones?.length ? store.contactPhones : [""],
          benefits: store.benefits?.length ? store.benefits : [""],
          status: store.status || "Active",
          address: store?.address?.formattedAddress || "",
          delivery_radius: store?.delivery_radius,
          location: store.location || "",
          city: store.city || "",
          logo: store.logo || "",
          operationalDays: store.operationalDays?.length
            ? store.operationalDays
            : [],
          operatingHours: operatingHours,
        };
        setStoreData(formattedData);
      }
    } catch (error) {
      console.error("Error fetching store data:", error);
    }
  };

  const handleFormChange = (changedValues) => {
    setStoreData((prev) => ({ ...prev, ...changedValues }));
  };

  const handleImageUpload = async (imageUrl) => {
    setStoreData((prev) => ({ ...prev, logo: imageUrl }));
  };

  const handleImageLoading = (isLoading) => {
    setImageLoading(isLoading);
  };

  const handleNotification = (type, message, description) => {
    api[type]({
      message,
      description,
    });
  };

  const handleStatusChange = (newStatus) => {
    setStoreData((prev) => ({ ...prev, status: newStatus }));
  };

  const onFinish = async (formValues) => {
    setLoading(true);

    try {
      // Format operating hours for only operational days
      const formattedOperatingHours = {};
      storeData.operationalDays.forEach((day) => {
        formattedOperatingHours[day] = {
          open: storeData.operatingHours[day]?.open
            ? dayjs(storeData.operatingHours[day].open).format("h:mm A") 
            : null,
          close: storeData.operatingHours[day]?.close
            ? dayjs(storeData.operatingHours[day].close).format("h:mm A")
            : null,
        };
      });

      const payload = {
        ...storeData,
        ...formValues,
        operatingHours: formattedOperatingHours,
        ...(location.state && { vendor_id: location.state }),
        status: storeData.status,
      };

      console.log(payload ,"payload ")
      if (idExist) {
        await apiHandle.post("edit-store", payload);
        success_toast_message("Store details updated successfully!");
      } else {
        await apiHandle.post("add-store", payload);
        success_toast_message("Store details saved successfully!");
      }

      if (user?.role === "Vendor") {
        fetchStoreData();
      }
    } catch (error) {
      console.log(error)
      error_toast_message(error.response?.data?.message ||"Failed to save store details");
    } finally {
      setLoading(false);
    }
  };

  if (!canRead) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Empty
          image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
          description={
            <span className="text-gray-600 text-lg">
              You don't have permission to view stores.
            </span>
          } 
        />
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      {contextHolder}
      <Card className="w-full shadow-lg rounded-xl overflow-hidden p-2">
        <Row gutter={24}>
          {/* Left Side - Store Form & Logo Upload */}
          <Col xs={24} md={12} className="overflow-y-auto max-h-[75vh] p-2">
            <Row gutter={16}>
              {/* Store Logo Upload */}
              <Col xs={24} className="text-center">
                <LogoUpload
                  logo={storeData.logo}
                  onImageChange={handleImageUpload}
                  onLoadingChange={handleImageLoading}
                  onNotification={handleNotification}
                  isLoading={imageLoading}
                  canUpdate={canUpdate}
                  apiHandle={apiHandle}
                />
              </Col>

              {/* Store Form */}
              <Col xs={24}>
                <StoreForm
                  canCreate={canCreate}
                  storeData={storeData}
                  onFormChange={handleFormChange}
                  onFinish={onFinish}
                  loading={loading}
                  canUpdate={canUpdate}
                  idExist={idExist}
                />
              </Col>
            </Row>
          </Col>

          {/* Right Side - Live Store Preview */}
          <Col xs={24} md={12} className="p-2">
            <div className="bg-gray-100 p-4 rounded-lg shadow-md">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <h4 className="text-lg font-semibold">Live Store Preview</h4>
                </div>

                <div>
                  <Select
                    size="middle"
                    value={storeData.status || "Active"}
                    onChange={handleStatusChange}
                    disabled={!canUpdate}
                  >
                    <Option value="Active">Active</Option>
                    <Option value="In-Active">Inactive</Option>
                  </Select>
                </div>
              </div>

              <StorePreviewCore
                storeData={storeData}
                vendorDetails={vendorDetails}
              />
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default Store;